import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, Star, Clock, Phone } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import SearchFilter from '../common/SearchFilter';
import Pagination from '../common/Pagination';
import ShopDetails from './ShopDetails';
import { fetchNearbyShops } from '../../services/api';
import toast from 'react-hot-toast';

const ShopList = () => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const [shops, setShops] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedShop, setSelectedShop] = useState(null);
  const [showShopDetails, setShowShopDetails] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('distance');

  useEffect(() => {
    getUserLocation();
    fetchNearbyShopsData();
  }, [currentUser]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [shops, searchTerm, filters, sortBy, userLocation]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Unable to get your location. Showing all shops.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser.');
    }
  };

  const fetchNearbyShopsData = async () => {
    try {
      setLoading(true);
      
      // Prepare API request data
      const requestData = {
        
      };

      // Call real API
      const response = await fetchNearbyShops(requestData);
      
      // Use the extractedData helper
      const shopsData = response.extractedData;
      
      if (shopsData?.shops) {
        // Process API response to match expected format
        const processedShops = shopsData.shops.map(shop => ({
          id: shop.id || shop._id,
          name: shop.name || shop.shopName,
          category: shop.category || 'General',
          rating: shop.rating || 4.0,
          distance: shop.distance || 0,
          address: shop.address || shop.location?.address || 'Address not available',
          phone: shop.phone || shop.contactNumber,
          isOpen: shop.isOpen !== undefined ? shop.isOpen : true,
          openHours: shop.openHours || shop.businessHours || '9:00 AM - 9:00 PM',
          location: {
            latitude: shop.location?.latitude || shop.lat || 0,
            longitude: shop.location?.longitude || shop.lng || 0
          },
          offers: shop.activeOffers || shop.offers?.length || 0,
          pointsPerRupee: shop.pointsPerRupee || shop.rewardRate || 1,
          image: shop.image || shop.imageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(shop.name || 'Shop')}&background=random`
        }));
        
        setShops(processedShops);
      } else {
        // Fallback to empty array if no shops found
        setShops([]);
        toast.info('No shops found in your area');
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
      toast.error('Failed to load shops. Please try again.');
      // Set empty array on error
      setShops([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...shops];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(shop =>
        shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shop.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shop.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (filters.category) {
      filtered = filtered.filter(shop => shop.category === filters.category);
    }
    if (filters.isOpen !== undefined) {
      filtered = filtered.filter(shop => shop.isOpen === filters.isOpen);
    }
    if (filters.minRating) {
      filtered = filtered.filter(shop => shop.rating >= parseFloat(filters.minRating));
    }
    if (filters.maxDistance) {
      filtered = filtered.filter(shop => shop.distance <= parseFloat(filters.maxDistance));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          return a.distance - b.distance;
        case 'rating':
          return b.rating - a.rating;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'offers':
          return b.offers - a.offers;
        default:
          return 0;
      }
    });

    setFilteredShops(filtered);
    setCurrentPage(1);
  };

  const openGoogleMaps = (shop) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${shop.location.latitude},${shop.location.longitude}`;
    window.open(url, '_blank');
  };

  const handleShopClick = (shop) => {
    setSelectedShop(shop);
    setShowShopDetails(true);
  };

  const filterOptions = [
    {
      key: 'category',
      label: 'Category',
      type: 'select',
      options: [
        { value: 'Cafe', label: 'Cafe' },
        { value: 'Restaurant', label: 'Restaurant' },
        { value: 'Clothing', label: 'Clothing' },
        { value: 'Electronics', label: 'Electronics' },
        { value: 'Books', label: 'Books' }
      ]
    },
    {
      key: 'isOpen',
      label: 'Status',
      type: 'select',
      options: [
        { value: true, label: 'Open Now' },
        { value: false, label: 'Closed' }
      ]
    },
    {
      key: 'minRating',
      label: 'Min Rating',
      type: 'select',
      options: [
        { value: '3', label: '3+ Stars' },
        { value: '4', label: '4+ Stars' },
        { value: '4.5', label: '4.5+ Stars' }
      ]
    },
    {
      key: 'maxDistance',
      label: 'Max Distance (km)',
      type: 'select',
      options: [
        { value: '1', label: 'Within 1 km' },
        { value: '2', label: 'Within 2 km' },
        { value: '5', label: 'Within 5 km' }
      ]
    }
  ];

  const sortOptions = [
    { value: 'distance', label: 'Distance' },
    { value: 'rating', label: 'Rating' },
    { value: 'name', label: 'Name' },
    { value: 'offers', label: 'Number of Offers' }
  ];

  // Pagination
  const totalPages = Math.ceil(filteredShops.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedShops = filteredShops.slice(startIndex, startIndex + itemsPerPage);

  const ShopCard = ({ shop }) => (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
      <div className="relative">
        <img
          src={shop.image}
          alt={shop.name}
          className="w-full h-48 object-cover"
        />
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            shop.isOpen 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {shop.isOpen ? 'Open' : 'Closed'}
          </span>
        </div>
        <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
          {shop.distance} km away
        </div>
      </div>
      
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-lg text-gray-900">{shop.name}</h3>
          <div className="flex items-center space-x-1">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span className="text-sm font-medium">{shop.rating}</span>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-2">{shop.category}</p>
        
        <div className="flex items-center space-x-1 text-sm text-gray-500 mb-2">
          <MapPin className="w-4 h-4" />
          <span className="truncate">{shop.address}</span>
        </div>
        
        <div className="flex items-center space-x-1 text-sm text-gray-500 mb-3">
          <Clock className="w-4 h-4" />
          <span>{shop.openHours}</span>
        </div>
        
        <div className="flex items-center justify-between text-sm mb-4">
          <span className="text-gray-600">
            {shop.offers} active offer{shop.offers !== 1 ? 's' : ''}
          </span>
          <span className="font-medium text-green-600">
            {shop.pointsPerRupee}x points per ₹
          </span>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={() => handleShopClick(shop)}
            className={`flex-1 ${theme.button} py-2 px-4 rounded-lg text-sm transition-colors`}
          >
            View Details
          </button>
          <button
            onClick={() => openGoogleMaps(shop)}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Navigation className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Finding nearby shops...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Nearby Shops</h1>
          <p className="text-gray-600">
            Discover shops near you and earn rewards on every purchase
          </p>
        </div>

        <SearchFilter
          onSearch={setSearchTerm}
          onFilter={setFilters}
          onSort={setSortBy}
          searchPlaceholder="Search shops, categories, or locations..."
          filters={filterOptions}
          sortOptions={sortOptions}
          initialSort="distance"
        />

        {filteredShops.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No shops found</h3>
            <p className="text-gray-600">
              Try adjusting your search criteria or check back later for new shops.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {paginatedShops.map((shop) => (
                <ShopCard key={shop.id} shop={shop} />
              ))}
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredShops.length}
            />
          </>
        )}
      </div>

      {/* Shop Details Modal */}
      {showShopDetails && selectedShop && (
        <ShopDetails
          shop={selectedShop}
          onClose={() => setShowShopDetails(false)}
        />
      )}
    </div>
  );
};

export default ShopList;